/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Persists an edited quantity for a single, already-saved Expense Inventory sampling
 * line item by reusing the createUpdateExpenseLineItems mutation's update branch
 * (identified by sfid/externalId), then refetches to resync display state.
 * @module src/components/Inventory/expense/detail/ExpenseInventorySampling/hook/useUpdateExpenseInventoryLineItemQuantity
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import CREATE_UPDATE_EXPENSE_LINE_ITEMS from '../../../../../../graphql/mutations/createUpdateExpenseLineItems';
import { NOTIFY_TYPE, STALE_RECORD_MESSAGE } from '../../../../../../util/constants';
import { isStaleRecordError } from '../../../../../../util/optimisticLock';
import { SamplingLineItem } from '../ExpenseInventorySamplingTable';

interface UseUpdateExpenseInventoryLineItemQuantityProps {
  expenseInventoryDetail: any;
  refetch: () => Promise<any>;
  openSnackBar: (type: string, message: string) => void;
}

const useUpdateExpenseInventoryLineItemQuantity = ({
  expenseInventoryDetail,
  refetch,
  openSnackBar,
}: UseUpdateExpenseInventoryLineItemQuantityProps) => {
  const [savingQuantityUniqueId, setSavingQuantityUniqueId] = useState<string | null>(null);
  const [createUpdateExpenseLineItems, { loading: loadingSaveQuantity }] = useMutation(
    CREATE_UPDATE_EXPENSE_LINE_ITEMS
  );

  const onQuantitySave = useCallback(
    async (item: SamplingLineItem, quantity: number): Promise<void> => {
      if (!item.sfid && !item.externalId) {
        return;
      }

      setSavingQuantityUniqueId(item.uniqueId);
      try {
        const result = await createUpdateExpenseLineItems({
          variables: {
            caseId: expenseInventoryDetail?.id,
            caseExternalId: expenseInventoryDetail?.caseExternalId,
            salesRepId: expenseInventoryDetail?.salesRepId,
            formType: 'New',
            lineItems: [
              {
                sfid: item.sfid,
                externalId: item.externalId,
                productId: item.productId,
                productNumber: item.productNumber,
                quantity: String(quantity ?? 0),
                lotNumber: item.lotNumber,
                isLotNumber: true,
                subInventoryCode: item.subInventoryCode || '',
                sourceLocation: item.sourceLocation,
              },
            ],
          },
        });

        if (result?.data?.createUpdateExpenseLineItems?.isMaxQuantityExceeded) {
          openSnackBar(
            NOTIFY_TYPE.ERROR,
            result.data.createUpdateExpenseLineItems.message ||
              'Error: The requested quantity exceeds what is available.'
          );
          return;
        }

        await refetch();
      } catch (error) {
        if (isStaleRecordError(error)) {
          openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
        } else {
          openSnackBar(
            NOTIFY_TYPE.ERROR,
            (error as Error)?.message || 'Error: Expense Inventory part could not be updated.'
          );
        }
      } finally {
        setSavingQuantityUniqueId(null);
      }
    },
    [expenseInventoryDetail, createUpdateExpenseLineItems, refetch, openSnackBar]
  );

  return { onQuantitySave, loadingSaveQuantity, savingQuantityUniqueId };
};

export default useUpdateExpenseInventoryLineItemQuantity;
